import { Component, Input, OnChanges, ViewChild } from '@angular/core'
import { ChartDataset, ChartOptions, ChartType } from 'chart.js/auto'
import { BaseChartDirective } from 'ng2-charts'

const DATA_POINTS = [
  'threeMonthPct',
  'calendarYTDPct',
  'oneYrPct',
  'threeYrPct',
  'fiveYrPct',
  'tenYrPct',
  'sinceInceptionPct',
]
const BAR_THICKNESS = 16
const QUARTER_END_GRIDLINE = 2
const MONTH_END_GRIDLINE = 3
const BORDER_WIDTH = 1
const LINE_WIDTH = 1
const PADDING = 24
const FONT_SIZE = 14
const MONTH_END_AVG_ANNUAL_RTN = 'monthEndAvgAnnualRtn'

@Component({
  selector: 'exemplar-bar-chart',
  templateUrl: './bar-chart.component.html',
  styleUrls: ['./bar-chart.component.scss'],
})
export class BarChartComponent implements OnChanges {
  @Input() data
  @Input() profileData
  @Input() dataType
  @ViewChild(BaseChartDirective) chart: BaseChartDirective
  canvasAccessibleName: string
  public barChartLabels: string[] = [
    'Month-end',
    '3-Month total',
    'YTD',
    '1-yr',
    '3-yr',
    '5-yr',
    '10-yr',
    'Since Inception',
  ]
  public barChartOptions: ChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        grid: {
          display: true,
          lineWidth: LINE_WIDTH,
          tickLength: 0,
          color: 'transparent',
        },
        ticks: {
          display: false,
        },
      },
      y: {
        grid: {
          lineWidth: 1,
          color: 'transparent',
        },
        ticks: {
          font: {
            family: 'FF Mark',
            style: 'normal',
            size: FONT_SIZE,
          },
          callback(value) {
            return `${value}%`
          },
        },
      },
    },
  }
  public barChartType: ChartType = 'bar'
  public barChartLegend = true

  public barChartData: ChartDataset[] = [
    {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      data: [],
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      label: '',
      barThickness: BAR_THICKNESS,
      backgroundColor: '#1BA39C',
      borderWidth: BORDER_WIDTH,
      borderColor: 'transparent',
    },
    {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      data: [],
      label: 'Benchmark',
      barThickness: BAR_THICKNESS,
      backgroundColor: '#C6E8E6',
      borderWidth: BORDER_WIDTH,
      borderColor: '#717777',
    },
  ]

  chartDescription: string

  // eslint-disable-next-line complexity, max-statements
  ngOnChanges(): void {
    this.setChartData()
    this.setChartLabels()
    this.setChartPadding()
    if (this.chart) {
      this.chart.update()
    }
  }

  ngAfterViewInit() {
    this.setChartPadding()
    if (this.chart) {
      this.chart.update()
    }
  }

  setChartData() {
    this.barChartData[0].data = []
    this.barChartData[1].data = []

    this.barChartData[0].label = this.profileData?.is529
      ? this.profileData?.shortName
      : this.profileData?.ticker

    if (this.dataType === MONTH_END_AVG_ANNUAL_RTN) {
      this.barChartData[0].data.push(this.data?.fundReturn?.prevMonthPct)
      this.barChartData[1].data.push(this.data?.benchmarkReturn?.prevMonthPct)
    }

    for (const value of DATA_POINTS) {
      this.barChartData[0].data.push(this.data?.fundReturn?.[value])
      this.barChartData[1].data.push(this.data?.benchmarkReturn?.[value])
    }
  }

  setChartLabels() {
    const labels = []
    const staticLabels = [
      'YTD',
      '1-yr',
      '3-yr',
      '5-yr',
      '10-yr',
      'Since inception',
    ]

    if (this.dataType === MONTH_END_AVG_ANNUAL_RTN) {
      labels.push('Month-end')
      labels.push('3-Month total')
      this.canvasAccessibleName = 'Summary Month-end bar chart'
    } else {
      labels.push('Quarter-end')
      this.canvasAccessibleName = 'Summary Quarter-end bar chart'
    }
    staticLabels.forEach((label) => labels.push(label))
    this.barChartLabels = labels
  }

  setChartPadding() {
    if (this.chart?.chart?.config?.options) {
      this.chart.chart.config.options.scales.y.ticks.padding = PADDING
      this.chart.chart.config.options.scales.x.grid.color = this.insertLine()
    }
  }

  insertLine() {
    const index =
      this.dataType !== MONTH_END_AVG_ANNUAL_RTN
        ? QUARTER_END_GRIDLINE
        : MONTH_END_GRIDLINE
    const temp = []
    for (let i = 0; i < this.barChartLabels.length + 1; i++) {
      temp.push(index === i ? '#717777' : 'transparent')
    }
    return temp
  }
}
